import { useEffect, useState, useCallback } from 'react';
import { Loader2, Users, AlertCircle, RefreshCw } from 'lucide-react';
import { api, extractApiErrorMessage } from '../../api/client';

interface Filho {
  id: string;
  nome: string;
  escola: string;
  turma: string;
  foto?: string;
  frequencia: number;
  statusHoje: 'PRESENTE' | 'AUSENTE' | 'ATRASADO' | 'INDEFINIDO';
  timeline: Array<{
    id: string;
    tipo: string;
    horario: string;
    descricao: string;
  }>;
}

export function FilhosTimeline() {
  const [filhos, setFilhos] = useState<Filho[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const res = await api.get('/mobile/responsaveis/meus-filhos');
      const data = res.data;
      setFilhos(Array.isArray(data) ? data : data?.filhos || []);
    } catch (err: any) {
      const msg = extractApiErrorMessage(err, 'Não foi possível carregar a lista de filhos vinculados.');
      setErro(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#7c3aed' }} />
      </div>
    );
  }

  if (erro) {
    return (
      <div className="m-empty m-animate" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div className="m-empty-icon" style={{ background: '#fee2e2', color: '#dc2626', margin: '0 auto 12px' }}>
          <AlertCircle size={26} />
        </div>
        <p style={{ fontWeight: 700, color: '#1e293b', marginBottom: 4, fontSize: '0.95rem' }}>
          Erro ao carregar dados
        </p>
        <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 16, lineHeight: 1.4, maxWidth: 300, margin: '0 auto 16px' }}>
          {erro}
        </p>
        <button
          type="button"
          onClick={() => void load()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            borderRadius: 10,
            border: 'none',
            background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '0.82rem',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
          }}
        >
          <RefreshCw size={14} />
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!filhos.length) {
    return (
      <div className="m-empty m-animate">
        <div className="m-empty-icon"><Users size={24} /></div>
        <p style={{ fontWeight: 600 }}>Nenhum filho vinculado</p>
        <p style={{ fontSize: '0.78rem' }}>Solicite na escola a vinculação</p>
      </div>
    );
  }

  const statusBadge = (s: string) => {
    if (s === 'PRESENTE') return <span className="m-badge m-badge-presente">Presente</span>;
    if (s === 'AUSENTE') return <span className="m-badge m-badge-ausente">Ausente</span>;
    if (s === 'ATRASADO') return <span className="m-badge m-badge-atrasado">Atrasado</span>;
    return <span className="m-badge" style={{ background: '#f1f5f9', color: '#64748b' }}>Sem registro</span>;
  };

  return (
    <div className="m-animate">
      <p className="m-section-title">Meus Filhos</p>

      {filhos.map((f) => (
        <div key={f.id} className="m-card">
          {/* Header do filho */}
          <div className="m-card-header">
            <div
              className="m-card-icon"
              style={{
                background: f.statusHoje === 'PRESENTE' ? '#dcfce7' : f.statusHoje === 'AUSENTE' ? '#fee2e2' : '#ede9fe',
                color: f.statusHoje === 'PRESENTE' ? '#16a34a' : f.statusHoje === 'AUSENTE' ? '#dc2626' : '#7c3aed',
                fontSize: '1rem',
                fontWeight: 800,
              }}
            >
              {f.nome.charAt(0)}
            </div>
            <div style={{ flex: 1 }}>
              <div className="m-card-title">{f.nome}</div>
              <div className="m-card-sub">{f.turma} · {f.escola}</div>
            </div>
            {statusBadge(f.statusHoje)}
          </div>

          {/* Frequência */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Frequência acumulada</span>
            <span style={{
              fontWeight: 800,
              fontSize: '0.85rem',
              color: f.frequencia >= 85 ? '#16a34a' : f.frequencia >= 75 ? '#d97706' : '#dc2626',
            }}>
              {f.frequencia}%
            </span>
          </div>
          <div className="m-freq-bar">
            <div
              className="m-freq-fill"
              style={{
                width: `${f.frequencia}%`,
                background: f.frequencia >= 85 ? '#16a34a' : f.frequencia >= 75 ? '#d97706' : '#dc2626',
              }}
            />
          </div>

          {/* Timeline do dia */}
          {f.timeline?.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <p className="m-section-title" style={{ marginBottom: 8 }}>Hoje</p>
              <div className="m-timeline">
                {f.timeline.map((ev) => (
                  <div key={ev.id} className="m-timeline-item">
                    <div className={`m-timeline-dot ${
                      ev.tipo === 'ENTRADA' ? 'm-timeline-dot-green'
                      : ev.tipo === 'SAIDA' ? 'm-timeline-dot-red'
                      : 'm-timeline-dot-blue'
                    }`} />
                    <div className="m-timeline-time">{ev.horario}</div>
                    <div className="m-timeline-text">{ev.descricao}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
